import { useState } from "react";

import { arcPath } from "@/lib/donut";
import { formatMinutes } from "@/lib/color";
import { cn } from "@/lib/utils";

export type DayChartSlice = {
  id: string;
  name: string;
  hex?: string;
  minutes: number;
};

/** One Label in the Ideal Day comparison. Percents are shares of planned work. */
export type IdealSlice = {
  id: string;
  name: string;
  hex?: string;
  idealPercent: number;
  actualPercent: number;
};

type Arc = {
  id: string;
  name: string;
  hex?: string;
  from: number;
  to: number;
  title: string;
};

function arcs(
  items: {
    id: string;
    name: string;
    hex?: string;
    value: number;
    title: string;
  }[],
): Arc[] {
  return items
    .filter((s) => s.value > 0)
    .reduce<{ out: Arc[]; used: number }>(
      (acc, s) => {
        acc.out.push({
          ...s,
          from: acc.used,
          to: Math.min(100, acc.used + s.value),
        });
        acc.used += s.value;
        return acc;
      },
      { out: [], used: 0 },
    ).out;
}

function Ring({
  c,
  outer,
  inner,
  items,
  muted,
}: {
  c: number;
  outer: number;
  inner: number;
  items: Arc[];
  muted?: boolean;
}) {
  const mid = (outer + inner) / 2;
  return (
    <>
      {items.map((a) =>
        a.to - a.from >= 100 ? (
          <circle
            key={a.id}
            cx={c}
            cy={c}
            r={mid}
            fill="none"
            stroke={a.hex ?? "var(--muted-foreground)"}
            strokeWidth={outer - inner}
            opacity={muted ? 0.5 : 1}
          >
            <title>{a.title}</title>
          </circle>
        ) : (
          <path
            key={a.id}
            d={arcPath(c, outer, inner, a.from, a.to)}
            fill={a.hex ?? "var(--muted-foreground)"}
            stroke="var(--card)"
            strokeWidth={2}
            opacity={muted ? 0.5 : 1}
          >
            <title>{a.title}</title>
          </path>
        ),
      )}
    </>
  );
}

/** Traffic-light reading of planned time against the Target Load band. */
type CapacityStatus = "under" | "in_band" | "over";

function capacityStatus(
  planned: number,
  targetMin: number,
  targetMax: number,
): CapacityStatus {
  if (planned > targetMax) return "over";
  if (planned < Math.min(targetMin, targetMax)) return "under";
  return "in_band";
}

/** Tailwind fill/stroke classes per status: orange / green / red. */
const statusClass: Record<CapacityStatus, { fill: string; stroke: string }> = {
  under: { fill: "fill-amber-500", stroke: "stroke-amber-500" },
  in_band: { fill: "fill-emerald-600", stroke: "stroke-emerald-600" },
  over: { fill: "fill-destructive", stroke: "stroke-destructive" },
};

const statusWord: Record<CapacityStatus, string> = {
  under: "room left",
  in_band: "well planned",
  over: "over planning",
};

/**
 * The Day chart (business-requirements.md § Today). 100% = the upper bound
 * of the Target Load band. Slices are planned minutes per display Label;
 * the grey remainder is capacity not yet planned. The band itself is read
 * as a traffic light: the centre percent and a thin outline around the ring
 * are green inside the band, orange below it (too little planned) and red
 * above its upper bound, when the ring also fills completely and the caller
 * shows the "Over planning" banner.
 *
 * With `ideal`, hovering (or clicking to pin) flips the chart to the Ideal
 * Day view: the ring becomes the Ideal Day mix and a thin outer ring shows
 * each Label's actual share of planned work, so the two can be compared at
 * a glance.
 */
export function DayChart({
  slices,
  targetMinutes,
  targetMinMinutes = targetMinutes,
  plannedMinutes,
  ideal,
  size = 220,
}: {
  slices: DayChartSlice[];
  /** Upper bound of the Target Load band: the ring's 100%. */
  targetMinutes: number;
  /** Lower bound of the band; defaults to the upper bound (single figure). */
  targetMinMinutes?: number;
  plannedMinutes: number;
  ideal?: IdealSlice[];
  size?: number;
}) {
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  const canFlip = !!ideal && ideal.length > 0;
  const showIdeal = canFlip && (hover || pinned);

  const c = size / 2;
  const edge = c - 2;
  // The status outline sits just outside the main ring.
  const outlineWidth = 3;
  const outlineGap = 3;
  const outer = edge - outlineWidth - outlineGap;
  const inner = outer * 0.64;
  const mid = (outer + inner) / 2;

  const status = capacityStatus(
    plannedMinutes,
    targetMinMinutes,
    targetMinutes,
  );
  const over = status === "over";
  const colors = statusClass[status];
  const denom = over ? plannedMinutes : targetMinutes;
  const planned = arcs(
    slices.map((s) => ({
      id: s.id,
      name: s.name,
      hex: s.hex,
      value: denom > 0 ? (s.minutes / denom) * 100 : 0,
      title: `${s.name} ${formatMinutes(s.minutes)}`,
    })),
  );
  const used = planned.length ? planned[planned.length - 1].to : 0;
  const fillPercent =
    targetMinutes > 0 ? Math.round((plannedMinutes / targetMinutes) * 100) : 0;

  // Ideal view geometry: main ring shrinks to leave room for the outer ring.
  const gap = 4;
  const outerRingWidth = 9;
  const idealOuter = edge - outerRingWidth - gap;
  const idealInner = idealOuter * 0.62;
  const idealArcs = arcs(
    (ideal ?? []).map((s) => ({
      id: s.id,
      name: s.name,
      hex: s.hex,
      value: s.idealPercent,
      title: `${s.name}: ideal ${s.idealPercent}%, planned ${s.actualPercent}%`,
    })),
  );
  const idealUsed = idealArcs.length ? idealArcs[idealArcs.length - 1].to : 0;
  const actualArcs = arcs(
    (ideal ?? []).map((s) => ({
      id: s.id,
      name: s.name,
      hex: s.hex,
      value: s.actualPercent,
      title: `${s.name}: planned ${s.actualPercent}% of today's work`,
    })),
  );

  const label = showIdeal
    ? `Ideal Day mix: ${(ideal ?? []).map((s) => `${s.name} ideal ${s.idealPercent}%, planned ${s.actualPercent}%`).join("; ")}`
    : targetMinutes > 0
      ? `${formatMinutes(plannedMinutes)} planned of ${formatMinutes(targetMinutes)} capacity` +
        (targetMinMinutes < targetMinutes
          ? ` (target ${formatMinutes(targetMinMinutes)}–${formatMinutes(targetMinutes)})`
          : "") +
        `, ${statusWord[status]}`
      : "No capacity set";

  return (
    <button
      type="button"
      className={cn(
        "block w-full rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring",
        canFlip ? "cursor-pointer" : "cursor-default",
      )}
      style={{ maxWidth: size }}
      aria-pressed={canFlip ? pinned : undefined}
      aria-label={canFlip ? "Toggle Ideal Day comparison" : undefined}
      title={
        canFlip
          ? showIdeal
            ? "Ideal Day vs planned"
            : "Hover or click to compare with your Ideal Day"
          : undefined
      }
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => canFlip && setPinned((p) => !p)}
      disabled={!canFlip}
    >
      <svg
        viewBox={`0 0 ${size} ${size}`}
        width="100%"
        role="img"
        aria-label={label}
      >
        {!showIdeal && (
          <>
            <circle
              cx={c}
              cy={c}
              r={mid}
              fill="none"
              strokeWidth={outer - inner}
              className="stroke-muted"
            />
            <Ring c={c} outer={outer} inner={inner} items={planned} />
            {used > 0 && used < 100 && (
              <path
                d={arcPath(c, outer, inner, used, 100)}
                className="fill-muted"
                stroke="var(--card)"
                strokeWidth={2}
              >
                <title>{`Unplanned ${formatMinutes(Math.max(0, targetMinutes - plannedMinutes))}`}</title>
              </path>
            )}
            {targetMinutes > 0 && (
              <circle
                cx={c}
                cy={c}
                r={edge - outlineWidth / 2}
                fill="none"
                strokeWidth={outlineWidth}
                className={colors.stroke}
              >
                <title>{`${statusWord[status]}: target ${
                  targetMinMinutes < targetMinutes
                    ? `${formatMinutes(targetMinMinutes)}–${formatMinutes(targetMinutes)}`
                    : formatMinutes(targetMinutes)
                }`}</title>
              </circle>
            )}
            <text
              x={c}
              y={c - 2}
              textAnchor="middle"
              className={cn(
                "text-2xl font-semibold",
                targetMinutes > 0 ? colors.fill : "fill-foreground",
              )}
            >
              {targetMinutes > 0 ? `${fillPercent}%` : "—"}
            </text>
            <text
              x={c}
              y={c + 16}
              textAnchor="middle"
              className="fill-muted-foreground text-[11px]"
            >
              {targetMinutes > 0
                ? `${plannedMinutes} / ${targetMinutes} min`
                : "no capacity"}
            </text>
          </>
        )}
        {showIdeal && (
          <>
            {/* Outer thin ring: actual share of planned work per Label. */}
            <circle
              cx={c}
              cy={c}
              r={edge - outerRingWidth / 2}
              fill="none"
              strokeWidth={outerRingWidth}
              className="stroke-muted"
            />
            <Ring
              c={c}
              outer={edge}
              inner={edge - outerRingWidth}
              items={actualArcs}
            />
            {/* Main ring: the Ideal Day mix. */}
            <circle
              cx={c}
              cy={c}
              r={(idealOuter + idealInner) / 2}
              fill="none"
              strokeWidth={idealOuter - idealInner}
              className="stroke-muted"
            />
            <Ring
              c={c}
              outer={idealOuter}
              inner={idealInner}
              items={idealArcs}
              muted
            />
            {idealUsed > 0 && idealUsed < 100 && (
              <path
                d={arcPath(c, idealOuter, idealInner, idealUsed, 100)}
                className="fill-muted"
                stroke="var(--card)"
                strokeWidth={2}
              >
                <title>No Ideal Day target</title>
              </path>
            )}
            <text
              x={c}
              y={c - 2}
              textAnchor="middle"
              className="fill-foreground text-base font-semibold"
            >
              Ideal Day
            </text>
            <text
              x={c}
              y={c + 14}
              textAnchor="middle"
              className="fill-muted-foreground text-[10px]"
            >
              outer ring = planned
            </text>
          </>
        )}
      </svg>
    </button>
  );
}
